import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
// Extensie is verplicht: package.json staat op "type": "module".
import { db, functions } from '../firebase.js';

/**
 * Data-toegang voor de automatische taken van een seizoen. Elke taak staat als een
 * los document onder `seasons/{seasonId}/automatedTasks/{taskId}` met zowel de config
 * als de laatste-run-status. De planner-Cloud-Function draait ze; deze module leest ze
 * en schrijft de config (het draaien zelf gebeurt via de callable `runAutomatedTask`).
 */

/** Weekdag-index zoals JavaScript hem kent: 0 = zondag … 6 = zaterdag. */
export const WEEKDAY_SHORT = { 0: 'Zo', 1: 'Ma', 2: 'Di', 3: 'Wo', 4: 'Do', 5: 'Vr', 6: 'Za' };

/** Weergavevolgorde: maandag eerst, zondag laatst. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const SYNC_INTERVALS = [1, 3, 5, 15];

/**
 * De taken die het spel kent, in weergavevolgorde. `defaults` vult een taak aan die
 * nog geen (volledig) document heeft, zodat de UI altijd werkt zonder backfill.
 */
export const TASK_DEFS = [
    {
        id: 'scoreSync',
        label: 'Scores bijwerken',
        description: 'Herrekent de spelerspunten rond de wedstrijden. Draait alleen tijdens en vlak na wedstrijden; buiten dat venster staat hij stil tot de eerstvolgende aftrap.',
        triggerType: 'matchWindow',
        defaults: { enabled: false, matchWindow: { intervalMinutes: 3, window: 'matchdays', hoursAfterFinish: 2 } },
    },
    {
        id: 'transferImport',
        label: 'Clubtransfers ophalen',
        description: 'Haalt de clubtransfers van de afgelopen dagen op uit Sportmonks en verwerkt ze in de spelers en teams van dit seizoen.',
        triggerType: 'schedule',
        defaults: { enabled: false, schedule: { weekdays: [0, 1, 2, 3, 4, 5, 6], times: ['07:00'] } },
    },
    {
        id: 'sidelinedSync',
        label: 'Blessures & schorsingen ophalen',
        description: 'Haalt blessures en schorsingen op uit Sportmonks voor alle clubs in dit seizoen.',
        triggerType: 'schedule',
        defaults: { enabled: false, schedule: { weekdays: [1, 4], times: ['08:00'] } },
    },
];

function tasksCollection(seasonId) {
    return collection(db, 'seasons', String(seasonId), 'automatedTasks');
}

function taskDoc(seasonId, taskId) {
    return doc(db, 'seasons', String(seasonId), 'automatedTasks', String(taskId));
}

/** Voegt de opgeslagen gegevens samen met de standaarden van de taakdefinitie. */
function mergeTask(def, data) {
    const merged = {
        id: def.id,
        label: def.label,
        description: def.description,
        triggerType: def.triggerType,
        enabled: data?.enabled ?? def.defaults.enabled,
        nextRunAt: data?.nextRunAt ?? null,
        lastRunAt: data?.lastRunAt ?? null,
        lastRunStatus: data?.lastRunStatus ?? null,
        lastRunSummary: data?.lastRunSummary ?? '',
        lastRunDurationMs: data?.lastRunDurationMs ?? 0,
    };
    if (def.triggerType === 'matchWindow') {
        merged.matchWindow = { ...def.defaults.matchWindow, ...(data?.matchWindow || {}) };
    } else {
        merged.schedule = { ...def.defaults.schedule, ...(data?.schedule || {}) };
    }
    return merged;
}

/**
 * Alle taken van een seizoen, aangevuld met de standaarden. Bestaat de `scoreSync`-taak
 * nog niet als document, dan valt hij terug op het oude `statsSync`-object van het
 * seizoen — dezelfde migratie die de planner server-side idempotent uitvoert.
 */
export async function fetchTasks(seasonId) {
    const snap = await getDocs(tasksCollection(seasonId));
    const byId = new Map(snap.docs.map((d) => [d.id, d.data()]));

    if (!byId.has('scoreSync')) {
        const seasonSnap = await getDoc(doc(db, 'seasons', String(seasonId)));
        const s = seasonSnap.exists() ? (seasonSnap.data().statsSync || {}) : {};
        if (Object.keys(s).length > 0) {
            byId.set('scoreSync', {
                enabled: s.enabled === true,
                matchWindow: { intervalMinutes: s.intervalMinutes, window: s.window, hoursAfterFinish: s.hoursAfterFinish },
                lastRunAt: s.lastRunAt || null,
                lastRunSummary: s.lastRunSummary || '',
                lastRunStatus: s.lastRunSummary ? 'success' : null,
            });
        }
    }

    return TASK_DEFS.map((def) => mergeTask(def, byId.get(def.id)));
}

/**
 * Schrijft de config van een taak weg. `nextRunAt` wordt gewist zodat de planner het
 * volgende run-moment opnieuw berekent; de laatste-run-velden en de run-log blijven
 * staan (setDoc met merge).
 */
export async function updateTaskConfig(seasonId, taskId, config) {
    const def = TASK_DEFS.find((t) => t.id === taskId);
    if (!def) throw new Error(`Onbekende taak: ${taskId}`);

    const payload = {
        taskId,
        triggerType: def.triggerType,
        enabled: config.enabled,
        nextRunAt: null,
        updatedAt: serverTimestamp(),
    };
    if (def.triggerType === 'matchWindow') payload.matchWindow = config.matchWindow;
    else payload.schedule = config.schedule;

    await setDoc(taskDoc(seasonId, taskId), payload, { merge: true });
}

/** De laatste runs van een taak, nieuwste eerst. */
export async function fetchTaskRuns(seasonId, taskId, max = 20) {
    const runsQuery = query(
        collection(db, 'seasons', String(seasonId), 'automatedTasks', String(taskId), 'runs'),
        orderBy('startedAt', 'desc'),
        limit(max),
    );
    const snap = await getDocs(runsQuery);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Voert een taak nu handmatig uit via de Cloud Function en geeft de samenvatting terug. */
export async function runTaskNow(seasonId, taskId) {
    const fn = httpsCallable(functions, 'runAutomatedTask');
    const result = await fn({ seasonId, taskId });
    return result.data;
}

/** Een leesbare beschrijving van wanneer een taak draait. */
export function formatSchedule(task) {
    if (task.triggerType === 'matchWindow') {
        const mw = task.matchWindow || {};
        return mw.window === 'always'
            ? `Elke ${mw.intervalMinutes} min · ook buiten speeldagen`
            : `Rond wedstrijden · elke ${mw.intervalMinutes} min`;
    }
    const sch = task.schedule || {};
    const dagen = WEEKDAY_ORDER.filter((d) => (sch.weekdays || []).includes(d)).map((d) => WEEKDAY_SHORT[d]);
    const dagLabel = dagen.length === 7 ? 'Dagelijks' : (dagen.length > 0 ? dagen.join(', ') : 'Geen dagen');
    const tijdLabel = (sch.times || []).length > 0 ? sch.times.join(', ') : 'geen tijd';
    return `${dagLabel} · ${tijdLabel}`;
}
